import { api } from './api';

export interface IngredienteTotalNecesario {
  ingrediente_id: number;
  nombre_ingrediente: string;
  cantidad_por_porcion: number;
  cantidad_total_necesaria: number;
  unidad: string;
  stock_disponible: number;
  suficiente: boolean;
}

export interface RecetaConsolidada {
  id: number;
  nombre: string;
  categoria: string;
  cantidadTotal: number;
  estado: 'pendiente' | 'en_preparacion' | 'terminado';
  fechaProduccion: string;
  fechaEntrega: string;
  pedidosRelacionados: number[];
  ingredientesFaltantes: string[];
  ingredientesTotales: IngredienteTotalNecesario[];
  tieneIngredientesFaltantes: boolean;
}

/** Item de la vista por segmento (Fase 5, 2026-06-19). */
export interface ItemPorSegmento {
  recetaId: number;
  recetaNombre: string;
  categoria: string;
  cantidadTotal: number;
  pedidoIds: number[];
  estado: 'pendiente' | 'en_preparacion' | 'terminado';
  esInsumo: boolean;
}

/** Agrupación por segmento devuelta cuando se pide ?por_segmento=true. */
export interface VistaPorSegmento {
  dia: { recetas: ItemPorSegmento[]; insumos: ItemPorSegmento[] };
  noche: { recetas: ItemPorSegmento[]; insumos: ItemPorSegmento[] };
  transversal: { recetas: ItemPorSegmento[]; insumos: ItemPorSegmento[] };
}

export interface PedidosProduccionResponse {
  fecha_preparacion: string;
  fecha_entrega: string;
  total_recetas: number;
  recetas: RecetaConsolidada[];
  pedidos_incluidos: Array<{
    id: number;
    cliente: string;
    fecha_entrega: Date;
    total_items: number;
  }>;
  /** Solo presente cuando se pidió ?por_segmento=true. */
  vistaPorSegmento?: VistaPorSegmento;
}

export interface ActualizarEstadoRequest {
  estado: 'pendiente' | 'en_preparacion' | 'terminado';
  fecha_preparacion: string;
}

export interface MetricasProduccion {
  periodo: {
    fecha_inicio: string;
    fecha_fin: string;
  };
  recetas_completadas: number;
  recetas_en_proceso: number;
  recetas_pendientes: number;
  eficiencia_diaria: number;
  tiempo_promedio_preparacion: number;
  ingredientes_mas_utilizados: any[];
}

class CocinaService {
  /**
   * Obtener pedidos para producción por fecha de preparación
   * @param fechaPreparacion Fecha en formato YYYY-MM-DD
   */
  async obtenerPedidosProduccion(
    fechaPreparacion: string,
    /** Cuando true, el backend agrega `vistaPorSegmento` al payload
     *  con la agrupación día/noche/transversal. Sin perjuicio de la
     *  lista plana `recetas`, que sigue presente para no romper a los
     *  consumidores que no esperan segmento. */
    porSegmento: boolean = false
  ): Promise<PedidosProduccionResponse> {
    try {
      const response = await api.get(`/cocina/pedidos-produccion`, {
        params: {
          fecha_preparacion: fechaPreparacion,
          ...(porSegmento ? { por_segmento: true } : {}),
        },
      });
      return response.data;
    } catch (error) {
      console.error('Error al obtener pedidos para producción:', error);
      throw error;
    }
  }

  /**
   * Obtener pedidos vencidos en cocina (fecha_entrega < hoy y todavía
   * en estados operativos: en_preparacion/listo). Misma estructura de
   * respuesta que obtenerPedidosProduccion para reusar el render.
   * @param hoy Fecha "hoy" en formato YYYY-MM-DD (zona del usuario).
   */
  async obtenerPedidosVencidos(
    hoy: string
  ): Promise<PedidosProduccionResponse> {
    try {
      const response = await api.get(`/cocina/pedidos-vencidos`, {
        params: { hoy },
      });
      return response.data;
    } catch (error) {
      console.error('Error al obtener pedidos vencidos:', error);
      throw error;
    }
  }

  /**
   * Actualizar estado de una receta en producción
   * @param recetaId ID de la receta
   * @param datos Estado y fecha de preparación
   */
  async actualizarEstadoReceta(
    recetaId: number,
    datos: ActualizarEstadoRequest
  ): Promise<{ success: boolean; message: string }> {
    try {
      console.log('🌐 DEBUG Service: Enviando petición HTTP');
      console.log('📡 URL:', `/cocina/recetas/${recetaId}/estado`);
      console.log('📦 Datos:', datos);

      const response = await api.patch(
        `/cocina/recetas/${recetaId}/estado`,
        datos
      );

      console.log('📨 Respuesta recibida:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Error al actualizar estado de receta:', error);
      throw error;
    }
  }

  /**
   * Obtener métricas de producción
   * @param fechaInicio Fecha de inicio opcional (YYYY-MM-DD)
   * @param fechaFin Fecha de fin opcional (YYYY-MM-DD)
   */
  async obtenerMetricasProduccion(
    fechaInicio?: string,
    fechaFin?: string
  ): Promise<MetricasProduccion> {
    try {
      const params: any = {};
      if (fechaInicio) params.fecha_inicio = fechaInicio;
      if (fechaFin) params.fecha_fin = fechaFin;

      const response = await api.get(`/cocina/metricas`, {
        params,
      });
      return response.data;
    } catch (error) {
      console.error('Error al obtener métricas de producción:', error);
      throw error;
    }
  }

  /**
   * Formatear fecha para usar en los endpoints (LOCAL)
   * @param fecha Objeto Date o string
   * @returns String en formato YYYY-MM-DD (Local)
   */
  formatearFecha(fecha: Date | string): string {
    if (typeof fecha === 'string') {
      return fecha.split('T')[0];
    }
    // Usar fecha local en lugar de UTC para evitar problemas de zona horaria
    const año = fecha.getFullYear();
    const mes = (fecha.getMonth() + 1).toString().padStart(2, '0');
    const día = fecha.getDate().toString().padStart(2, '0');
    return `${año}-${mes}-${día}`;
  }

  /**
   * Obtener fecha para cocina con offset personalizable - LOCAL
   * @param offsetDias Número de días a agregar (puede ser negativo: -1 = ayer, 0 = hoy, 1 = mañana)
   * @returns Fecha en formato YYYY-MM-DD (Local)
   */
  obtenerFechaPreparacion(offsetDias: number = 0): string {
    const fecha = new Date();
    // Usar fecha local directa para coincidir con la zona horaria del usuario
    fecha.setDate(fecha.getDate() + offsetDias);
    return this.formatearFecha(fecha);
  }
}

export const cocinaService = new CocinaService();
export default cocinaService;
